import type { ReactNode } from 'react';
import { CheckCircle2, ShieldCheck, X } from 'lucide-react';

type AdminActionDialogProps = {
  open: boolean;
  eyebrow?: string;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  details?: string[];
  footerNote?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
};

export function AdminActionDialog({
  open,
  eyebrow = 'Admin action',
  title,
  description,
  confirmLabel,
  cancelLabel = 'Not now',
  details = [],
  footerNote,
  onCancel,
  onConfirm,
}: AdminActionDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-action-dialog-title"
        className="w-full max-w-xl overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-2xl shadow-slate-950/20 ring-1 ring-slate-900/5"
      >
        <div className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50/70 px-6 pb-5 pt-6 sm:px-7">
          <button
            type="button"
            onClick={onCancel}
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-white hover:text-slate-800"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-4 pr-8">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-950 text-white shadow-lg shadow-blue-950/15">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">{eyebrow}</p>
              <h2 id="admin-action-dialog-title" className="mt-2 text-2xl font-bold tracking-tight text-slate-950">
                {title}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
            </div>
          </div>
        </div>

        {details.length ? (
          <div className="space-y-2 px-6 py-5 sm:px-7">
            {details.map(detail => (
              <div key={detail} className="flex gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-3 text-sm leading-5 text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span>{detail}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="border-t border-slate-100 bg-slate-50/70 px-6 py-5 sm:px-7">
          {footerNote ? <div className="mb-4 text-xs leading-5 text-slate-500">{footerNote}</div> : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex items-center justify-center rounded-2xl bg-blue-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-blue-950/10 transition hover:bg-blue-900"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
